/**
 * ArcDiscountBadge — V1.1 visual utility activation.
 *
 * STRICT SCOPE: cosmetic only. This component reads tier + fee discount from
 * the ArcEcosystemContext and renders a chip describing what discount the
 * connected wallet WOULD receive when V1.2 fee math is activated.
 *
 * IT DOES NOT:
 *   - modify any fee calculation
 *   - alter settlement, payout, or wallet ledger
 *   - send transactions, request signatures, or invoke approvals
 *   - hit the blockchain on its own (purely consumes context)
 *
 * Variants:
 *   - "inline"  : compact pill suitable beside an "Enter" / "Buy entry" CTA
 *   - "block"   : larger card-style; future use on entry-confirm screens
 */

import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Wallet, Lock, AlertTriangle, ArrowUpRight } from "lucide-react";
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from "@/components/ui/tooltip";
import {
  useArcWallet, useArcStatus, useArcTier, useArcFeeDiscount, useArcFeeDiscountActive,
} from "@/context/ArcEcosystemContext";

const fmt = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });

/**
 * Resolve a presentational state from the ecosystem context.
 * Pure function — easy to extend without changing the component.
 */
function resolveBadgeState({ wallet, status, tier, discount, isActive }) {
  if (!wallet?.address) return { kind: "disconnected" };
  if (status.wrongNetwork) return { kind: "wrong-network" };
  if (status.isPreLaunch) return { kind: "pre-launch" };
  if (!tier?.current || discount === 0) {
    const nextMin = tier?.next?.min_arc;
    const nextName = tier?.next?.name;
    return { kind: "upgrade", nextMin, nextName };
  }
  // V1.2 split: real activation vs cosmetic preview
  return {
    kind: isActive ? "active-live" : "active",
    tier: tier.current,
    discountPct: discount,
  };
}

export default function ArcDiscountBadge({ variant = "inline", className = "", testId }) {
  const { wallet, connect } = useArcWallet();
  const status = useArcStatus();
  const tier = useArcTier();
  const discount = useArcFeeDiscount();
  const { isActive } = useArcFeeDiscountActive();

  const state = resolveBadgeState({ wallet, status, tier, discount, isActive });

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={className}
            data-testid={testId}
            data-state={state.kind}
          >
            <span data-testid={`arc-discount-badge-${state.kind}`}>
              <BadgeBody state={state} variant={variant} onConnect={() => safeWalletClick(connect)} />
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="center"
          className="bg-[#0a0b10] border border-[#39FF14]/30 text-white font-mono text-[11px] max-w-xs"
        >
          <TooltipBody state={state} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/* -------------------------- visual body -------------------------- */

function BadgeBody({ state, variant, onConnect }) {
  const sizing = variant === "block"
    ? "px-3 py-2 text-xs"
    : "px-2.5 py-1 text-[10px]";

  if (state.kind === "disconnected") {
    return (
      <button
        type="button"
        onClick={onConnect}
        className={`${sizing} font-mono uppercase tracking-[0.22em] inline-flex items-center gap-1.5 border border-white/15 text-white/55 hover:text-[#39FF14] hover:border-[#39FF14]/40 transition-colors`}
        data-testid="arc-badge-action-connect"
      >
        <Wallet className="w-3 h-3" />
        Connect for ARC discount
      </button>
    );
  }

  if (state.kind === "wrong-network") {
    return (
      <span className={`${sizing} font-mono uppercase tracking-[0.22em] inline-flex items-center gap-1.5 border border-[#FF3B30]/40 text-[#FF3B30]`}>
        <AlertTriangle className="w-3 h-3" />
        Switch to BSC for ARC discount
      </span>
    );
  }

  if (state.kind === "pre-launch") {
    return (
      <span className={`${sizing} font-mono uppercase tracking-[0.22em] inline-flex items-center gap-1.5 border border-[#F59E0B]/40 text-[#F59E0B]`}>
        <Lock className="w-3 h-3" />
        ARC discount · unlocks at TGE
      </span>
    );
  }

  if (state.kind === "upgrade") {
    const label = state.nextMin
      ? `Hold ${fmt(state.nextMin)} ARC → ${state.nextName} discount`
      : "Hold ARC for fee discounts";
    return (
      <span className={`${sizing} font-mono uppercase tracking-[0.22em] inline-flex items-center gap-1.5 border border-white/15 text-white/55`}>
        <ArrowUpRight className="w-3 h-3" />
        {label}
      </span>
    );
  }

  // Active tier discount — covers both V1.1 cosmetic ("active") and V1.2 live ("active-live")
  const t = state.tier;
  const live = state.kind === "active-live";
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={`${t.id}-${state.kind}`}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.92 }}
        transition={{ duration: 0.25 }}
        className={`${sizing} font-mono uppercase tracking-[0.22em] inline-flex items-center gap-1.5`}
        style={{
          color: t.color,
          border: `1px solid ${t.color}${live ? "" : "66"}`,
          background: `${t.color}${live ? "1a" : "10"}`,
          boxShadow: live
            ? `0 0 18px ${t.color}55, inset 0 0 0 1px ${t.color}33`
            : `0 0 12px ${t.color}33, inset 0 0 0 1px ${t.color}12`,
        }}
      >
        <Sparkles className="w-3 h-3" />
        {labelFor(t)} <span className="font-bold">−{state.discountPct}%</span>
        {live && <span className="font-bold ml-0.5">· Applied</span>}
      </motion.span>
    </AnimatePresence>
  );
}

function labelFor(t) {
  if (t.id === "challenger") return "ARC Holder";
  if (t.id === "elite") return "Elite ARC";
  if (t.id === "master") return "Arena Master";
  return t.name;
}

/* -------------------------- tooltip body -------------------------- */

function TooltipBody({ state }) {
  if (state.kind === "active-live") {
    return (
      <div className="leading-relaxed">
        <div className="text-[#39FF14] font-bold mb-1">Discount Applied</div>
        <div className="text-white/75">
          Your <span className="font-bold" style={{ color: state.tier.color }}>{state.tier.name}</span>{" "}
          tier is now reducing your platform commission by{" "}
          <span className="font-bold">−{state.discountPct}%</span>.
        </div>
        <div className="mt-1.5 text-white/40 text-[10px]">
          Live · platform commission only · prize pool unaffected
        </div>
      </div>
    );
  }
  if (state.kind === "active") {
    return (
      <div className="leading-relaxed">
        <div className="text-[#39FF14] font-bold mb-1">Future Arena Fee Discount</div>
        <div className="text-white/75">
          Your <span className="font-bold" style={{ color: state.tier.color }}>{state.tier.name}</span>{" "}
          tier qualifies for a <span className="font-bold">−{state.discountPct}%</span> commission
          discount when V1.2 fee activation goes live.
        </div>
        <div className="mt-1.5 text-white/40 text-[10px]">
          V1.1 · cosmetic display only · settlement math unchanged
        </div>
      </div>
    );
  }
  if (state.kind === "upgrade") {
    return (
      <div className="leading-relaxed text-white/75">
        Connect more ARC to unlock fee discounts. Reach the
        {" "}<span className="font-bold text-[#39FF14]">{state.nextName}</span> tier
        ({fmt(state.nextMin || 0)} ARC) for your first commission discount.
      </div>
    );
  }
  if (state.kind === "pre-launch") {
    return (
      <div className="leading-relaxed text-white/75">
        ARC contract is pre-launch. Once TGE happens and your wallet holds ARC,
        this badge will reflect your tier-based commission discount automatically.
      </div>
    );
  }
  if (state.kind === "wrong-network") {
    return (
      <div className="leading-relaxed text-white/75">
        ARC discounts read your balance from BNB Smart Chain. Switch your wallet
        to BSC to surface the discount you qualify for.
      </div>
    );
  }
  return (
    <div className="leading-relaxed text-white/75">
      Connect your wallet — we'll read your ARC balance and surface the future
      Arena commission discount your tier qualifies for. Read-only, no
      signatures, no approvals.
    </div>
  );
}
